/**
 * ProviderRegistrationPage Component
 *
 * Main page component for the provider registration workflow.
 * Integrates with the RegistrationWizard and existing Layout components.
 * Provides responsive design and SEO optimization.
 *
 * @module components/pages/ProviderRegistrationPage
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/utils/cn';
import { Layout } from '@/components/layout/Layout';
import { RegistrationWizard } from '@/components/provider-registration/RegistrationWizard';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import type { RegistrationStep } from '@/types/registration';
import { BodyText, TitleText } from '../ui';

/**
 * ProviderRegistrationPage component props interface
 */
export interface ProviderRegistrationPageProps {
  /** Custom class name */
  className?: string;
  /** Callback when registration completes */
  onRegistrationComplete?: () => void;
  /** Callback when user wants to contact support */
  onContactSupport?: () => void;
  /** Callback when user wants to go to dashboard */
  onGoToDashboard?: () => void;
}

/**
 * Step-specific content for SEO and accessibility
 */
const STEP_CONTENT = {
  registration: {
    title: 'Create Your Provider Account',
    description: 'Start your journey with BiteWise by creating your provider account'
  },
  profile_update: {
    title: 'Complete Your Professional Profile',
    description: 'Add your practice details and provider credentials'
  },
  email_verification: {
    title: 'Verify Your Email',
    description: 'Check your email and verify your account'
  },
  complete: {
    title: 'Registration Complete',
    description: 'Welcome to BiteWise! Your registration is complete'
  }
};

/**
 * ProviderRegistrationPage component for the complete registration workflow
 *
 * @example
 * ```tsx
 * <ProviderRegistrationPage
 *   onRegistrationComplete={() => {
 *     analytics.track('provider_registration_complete');
 *     navigate('/provider/dashboard');
 *   }}
 * />
 * ```
 */
export const ProviderRegistrationPage: React.FC<ProviderRegistrationPageProps> = ({
  className,
  onRegistrationComplete,
  onContactSupport
}) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('registration');

  /**
   * Handle step changes for analytics and SEO
   */
  const handleStepChange = (step: RegistrationStep) => {
    setCurrentStep(step);

    // Update page title for better UX
    const stepContent = STEP_CONTENT[step];
    if (stepContent && typeof document !== 'undefined') {
      document.title = `${stepContent.title} - BiteWise`;
    }
  };

  /**
   * Handle registration completion
   */
  const handleRegistrationComplete = () => {
    onRegistrationComplete?.();
  };

  /**
   * Get current step content for accessibility
   */
  const getCurrentStepContent = () => {
    return STEP_CONTENT[currentStep] || STEP_CONTENT.registration;
  };

  const stepContent = getCurrentStepContent();

  return (
    <>
      <Layout className={cn('min-h-screen', className)}>
        {/* Page Header */}
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto text-center">
              <TitleText>
                {stepContent.title}
              </TitleText>
              <BodyText
                variant="secondary"
                className="text-lg"
              >
                {stepContent.description}
              </BodyText>

              {/* Progress Indicator for Screen Readers */}
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                Currently on step: {currentStep.replace('_', ' ')}
              </div>
            </div>
          </div>

        {/* Main Content */}
        <main className={cn(
          "flex-1",
          isDark ? "bg-secondary-900" : "bg-white"
        )}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            {/* Registration Wizard */}
            <RegistrationWizard
              onComplete={handleRegistrationComplete}
              onStepChange={handleStepChange}
              className="max-w-4xl mx-auto"
            />

            {/* Additional Information */}
            {currentStep !== 'complete' && (
              <div className="max-w-2xl mx-auto mt-4">
                <div className={cn(
                  "rounded-lg p-6",
                  isDark ? "bg-secondary-900" : "bg-secondary-50"
                )}>
                  <h2 className={cn(
                    "text-lg font-semibold mb-3",
                    isDark ? "text-secondary-100" : "text-secondary-900"
                  )}>
                    Why Join BiteWise?
                  </h2>
                  <ul className={cn(
                    "space-y-2 text-sm",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Connect with qualified, verified dietitians in your area
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Streamlined referral process with digital tracking
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Improved patient outcomes through personalised nutrition plans
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      No cost to join - earn referral credits for successful matches
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Support Information and Action Buttons - Only show when completed */}
            {currentStep === 'complete' && (
              <div className="max-w-2xl mx-auto mt-12">
                {/* Support Information */}
                <div className="text-center mb-8">
                  <p className={cn(
                    "text-sm mb-6",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    If you have any questions about your registration or the approval process, please don't hesitate to contact our support team.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={onContactSupport}
                      className="sm:w-auto"
                    >
                      Contact Support
                    </Button>

                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => {
                        // Reset navigation stack to home, then navigate to dashboard
                        navigate('/', { replace: true });
                        navigate('/dashboard');
                      }}
                      className="sm:w-auto"
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </Layout>
    </>
  );
};

export default ProviderRegistrationPage;
